import { cn } from "@/lib/utils";
import type { UIMessage } from "@ai-sdk/react";
import equal from "fast-deep-equal";
import { motion } from "framer-motion";
import { Sparkles, User } from "lucide-react";
import Markdown from "markdown-to-jsx";
import { memo } from "react";
import { MessageActions } from "./message-actions";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { useDataStream } from "./ui/data-stream-provider";

interface MessageProps {
  message: UIMessage;
  isLoading?: boolean;
}

const CONTENT = `
Okay, here's a brief for an investor regarding "Zodinet," assuming it's a technology company focused on next-generation connectivity and infrastructure.

---

**ZODINET: Investor Brief**

**Tagline:** Powering the Future of Decentralized, Secure, and High-Performance Digital Infrastructure.

**1. Executive Summary & The Opportunity**
The digital world is at an inflection point, demanding unprecedented levels of security, speed, and privacy. Current internet infrastructure, built for a different era, is struggling to keep pace with the demands of Web3, AI, IoT, and the Metaverse. Zodinet is building the foundational layer for this new digital paradigm: a cutting-edge, decentralized network fabric designed for ultra-low latency, quantum-resistant security, and unparalleled scalability. We are poised to capture a significant share of the rapidly expanding market for next-gen connectivity and secure data exchange.

**2. The Problem**
*   **Centralization & Vulnerability:** Current internet architecture is highly centralized, making it susceptible to large-scale data breaches, censorship, and single points of failure.
*   **Performance Bottlenecks:** Traditional networks struggle with the massive data transfer and real-time processing required by AI, high-fidelity VR/AR, and industrial IoT.
*   **Privacy & Data Ownership:** Users and businesses lack true control over their data, leading to trust issues and regulatory complexities.
*   **Security Gaps:** Existing encryption methods are becoming increasingly vulnerable to advanced cyber threats, including potential quantum computing attacks.

**3. Zodinet's Innovative Solution**
Zodinet is developing a proprietary, multi-layered network infrastructure that combines:
*   **Decentralized Mesh Networking:** Utilizing a distributed node architecture to enhance resilience, bypass censorship, and distribute data efficiently.
*   **AI-Optimized Routing:** Leveraging machine learning to dynamically optimize network paths for speed, efficiency, and resource allocation.
*   **Quantum-Resistant Encryption:** Implementing advanced cryptographic protocols designed to secure data against future quantum computing threats, ensuring long-term data integrity.
*   **Edge Computing Integration:** Pushing processing power closer to the data source, drastically reducing latency for real-time applications.
*   **Built-in Privacy & Ownership:** Architected from the ground up to empower users and enterprises with granular control over their data and identity.

**4. Market Opportunity**
The demand for secure, high-performance, and decentralized digital infrastructure is exploding across multiple sectors:
*   **Web3 & Blockchain:** Powering dApps, DeFi, NFTs, and the broader decentralized economy.
*   **Artificial Intelligence & Machine Learning:** Providing the robust, low-latency data pipes essential for AI training and inference.
*   **Internet of Things (IoT):** Securing and optimizing connectivity for billions of connected devices in smart cities, industrial automation, and healthcare.
*   **Metaverse & XR:** Delivering the high-bandwidth, real-time interactivity required for immersive digital experiences.
*   **Enterprise Security:** Offering a superior, future-proof solution for corporate data protection and communication.

The combined market for these sectors represents a multi-trillion-dollar opportunity, with significant growth projected over the next decade.

**5. Competitive Advantage**
Zodinet's differentiation lies in its holistic approach, integrating **decentralization, AI optimization, and quantum-resistant security** into a single, cohesive network fabric. While competitors may offer pieces of this puzzle, Zodinet's unified architecture provides:
*   **Superior Security:** Proactive defense against current and future threats.
*   **Unrivaled Performance:** Ultra-low latency and high throughput for demanding applications.
*   **True Scalability:** Designed to expand seamlessly with demand.
*   **Future-Proof Technology:** Addressing emerging challenges like quantum computing.
*   **Enhanced Privacy & Data Sovereignty:** A core design principle, not an afterthought.

**6. Business Model**
Zodinet will generate revenue through a multi-faceted model:
*   **Subscription & Licensing:** Tiered access to Zodinet's network services for enterprises, developers, and institutions (SaaS model).
*   **Usage-Based Fees:** Data transfer and processing fees for high-volume applications.
*   **Infrastructure Partnerships:** Licensing Zodinet's technology and protocols to existing network providers and hardware manufacturers.
*   **(Optional: Tokenomics):** A native utility token could incentivize network participation, secure operations, and facilitate micro-transactions within the Zodinet ecosystem.

**7. Team**
Our visionary leadership team comprises seasoned experts in network architecture, advanced cryptography, artificial intelligence, and scalable software development, with a proven track record of innovation and execution in leading tech firms.

**8. Traction & Milestones (Examples - *To be customized with actual data*)**
*   Successful completion of a robust proof-of-concept demonstrating key technological capabilities.
*   Strategic partnerships secured with [X] industry leaders for pilot programs.
*   Initial intellectual property filed covering core network protocols and security innovations.
*   MVP expected by Q4 [Year].

**9. The Ask**
Zodinet is seeking **[X amount] in [Seed/Series A] funding** to:
*   Accelerate R&D and finalize core technology development.
*   Expand our engineering and product teams.
*   Initiate comprehensive market penetration strategies and build out enterprise partnerships.
*   Scale network infrastructure and operational capabilities.

**10. Call to Action**
Join Zodinet as we build the secure, high-performance, and decentralized digital foundation for the next generation of the internet. We invite you to a deeper dive into our technology and strategic roadmap.

---
`;
const ProductListCard = ({ products }: { products: any }) => {
  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex gap-3 min-w-max">
        {products.map((product: any) => (
          <ProductCard
            key={`product-${product.id}`}
            product={product}
          />
        ))}
      </div>
    </div>
  );
};

const ProductCard = ({ product }: { product: any }) => {
  return (
    <div
      style={{ backgroundColor: product.backgroundColor }}
      className="bg-white dark:bg-zinc-800 rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow w-[200px] flex-shrink-0"
    >
      {product.photo && (
        <img
          src={`https://static.nuvioo.app/${product.photo}`}
          alt={product.name}
          className="w-full h-40 object-cover rounded-lg mb-3"
        />
      )}
      <p className="text-center text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-2">
        {product.name}
      </p>
      <p className="text-center text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-2">
        {new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(product.price / 100)}
      </p>
    </div>
  );
};

function PureMessage({ message, isLoading = false }: MessageProps) {
  const isUser = message.role === "user";

  useDataStream();

  // Get content from parts - AI SDK v2 uses parts structure
  const content =
    message.parts
      ?.map((part) => {
        if (part.type === "text") return part.text;
        return "";
      })
      .join("") || "";

  return (
    <motion.div
      className={cn(
        "flex gap-4 w-full group",
        isUser ? "justify-end" : "justify-start"
      )}
      data-role={message.role}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {!isUser && (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2, delay: 0.1 }}
        >
          <Avatar className="flex-shrink-0">
            <AvatarFallback className="bg-primary text-primary-foreground">
              <Sparkles className="w-4 h-4" />
            </AvatarFallback>
          </Avatar>
        </motion.div>
      )}

      <div
        className={cn("flex flex-col gap-2 max-w-[80%]", isUser && "items-end")}
      >
        <motion.div
          className={cn(
            "rounded-2xl px-4 py-3 break-words",
            isUser
              ? "bg-primary text-primary-foreground rounded-br-sm"
              : "bg-muted text-foreground rounded-bl-sm"
          )}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2, delay: 0.15 }}
        >
          <div className="prose prose-sm max-w-none dark:prose-invert">
            {message.parts?.map((part, index) => {
              const { type } = part;
              const key = `message-${message.id}-part-${index}`;

              if (type === "tool-searchProducts") {
                if (part.state === "input-streaming") {
                }
                if (part.state === "input-available") {
                }
                if (part.state === "output-available") {
                  const { output } = part;
                  return (
                    <ProductListCard key={key} products={(output as any).products} />
                  );
                }
                if (part.state === "output-error") {
                }
              }

              if (type === "text") {
                return (
                  <Markdown
                    key={key}
                    options={{
                      overrides: {
                        p: {
                          component: "p",
                          props: {
                            className: "mb-2 last:mb-0 text-sm leading-relaxed",
                          },
                        },
                        code: {
                          component: ({
                            className,
                            children,
                            ...props
                          }: any) => {
                            const isBlock = className?.includes("lang-");
                            return isBlock ? (
                              <pre className="bg-zinc-900/50 dark:bg-zinc-900 rounded-lg p-3 overflow-x-auto my-2">
                                <code
                                  className={cn("text-xs", className)}
                                  {...props}
                                >
                                  {children}
                                </code>
                              </pre>
                            ) : (
                              <code
                                className="bg-zinc-200 dark:bg-zinc-800 px-1.5 py-0.5 rounded text-xs"
                                {...props}
                              >
                                {children}
                              </code>
                            );
                          },
                        },
                        ul: {
                          component: "ul",
                          props: {
                            className:
                              "list-disc list-inside mb-2 last:mb-0 text-sm space-y-1",
                          },
                        },
                        ol: {
                          component: "ol",
                          props: {
                            className:
                              "list-decimal list-inside mb-2 last:mb-0 text-sm space-y-1",
                          },
                        },
                        li: {
                          component: "li",
                          props: {
                            className: "text-sm",
                          },
                        },
                        h1: {
                          component: "h1",
                          props: {
                            className: "text-lg font-bold mb-2 mt-4 first:mt-0",
                          },
                        },
                        h2: {
                          component: "h2",
                          props: {
                            className:
                              "text-base font-bold mb-2 mt-3 first:mt-0",
                          },
                        },
                        h3: {
                          component: "h3",
                          props: {
                            className: "text-sm font-bold mb-2 mt-2 first:mt-0",
                          },
                        },
                        blockquote: {
                          component: "blockquote",
                          props: {
                            className:
                              "border-l-4 border-zinc-300 dark:border-zinc-700 pl-4 italic my-2",
                          },
                        },
                        a: {
                          component: "a",
                          props: {
                            className:
                              "text-blue-600 dark:text-blue-400 underline hover:no-underline",
                            target: "_blank",
                            rel: "noopener noreferrer",
                          },
                        },
                        table: {
                          component: ({ children, ...props }: any) => (
                            <div className="overflow-x-auto my-2">
                              <table
                                className="min-w-full divide-y divide-zinc-300 dark:divide-zinc-700 text-sm"
                                {...props}
                              >
                                {children}
                              </table>
                            </div>
                          ),
                        },
                        th: {
                          component: "th",
                          props: {
                            className:
                              "px-3 py-2 text-left font-semibold bg-zinc-100 dark:bg-zinc-800",
                          },
                        },
                        td: {
                          component: "td",
                          props: {
                            className:
                              "px-3 py-2 border-t border-zinc-200 dark:border-zinc-700",
                          },
                        },
                      },
                    }}
                  >
                    {content}
                  </Markdown>
                );
              }

              // return <div key={key}>{JSON.stringify(part, null, 2)}</div>;
            })}
          </div>
        </motion.div>

        {!isUser && !isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, delay: 0.3 }}
          >
            <MessageActions message={message} content={content} />
          </motion.div>
        )}
      </div>

      {isUser && (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2, delay: 0.1 }}
        >
          <Avatar className="flex-shrink-0">
            <AvatarFallback className="bg-secondary">
              <User className="w-4 h-4" />
            </AvatarFallback>
          </Avatar>
        </motion.div>
      )}
    </motion.div>
  );
}

export const Message = memo(PureMessage, (prevProps, nextProps) => {
  if (prevProps.isLoading !== nextProps.isLoading) return false;
  if (prevProps.message.id !== nextProps.message.id) return false;

  if (!equal(prevProps.message.parts, nextProps.message.parts)) return false;

  return false;
});

interface ThinkingMessageProps {
  className?: string;
}

export function ThinkingMessage({ className }: ThinkingMessageProps) {
  return (
    <motion.div
      className={cn("flex gap-4 w-full", className)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2, delay: 0.1 }}
      >
        <Avatar className="flex-shrink-0">
          <AvatarFallback className="bg-primary text-primary-foreground">
            <Sparkles className="w-4 h-4" />
          </AvatarFallback>
        </Avatar>
      </motion.div>

      <div className="flex flex-col gap-2 max-w-[80%]">
        <motion.div
          className="rounded-2xl px-4 py-3 bg-muted text-foreground rounded-bl-sm"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2, delay: 0.15 }}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Thinking</span>
            <div className="flex gap-1">
              <span
                className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce"
                style={{ animationDelay: "0ms" }}
              />
              <span
                className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce"
                style={{ animationDelay: "150ms" }}
              />
              <span
                className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce"
                style={{ animationDelay: "300ms" }}
              />
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
